import { Injectable } from '@angular/core';
import { ItemResource, Link } from '@ngx-hal/models';
import { ItemResourceWebService } from '@ngx-hal/web-services';
import { Action, createSelector, State, StateContext } from '@ngxs/store';
import { EMPTY, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ItemResourceCreate, ItemResourceRead, ItemResourceRemove, ItemResourceUpdate } from './item-resource.actions';

type ItemResourceStateModel<Entity extends ItemResource> = Map<string, Entity | undefined> | undefined;

@State<ItemResourceStateModel<ItemResource>>({
  name: 'itemResource',
  defaults: null as unknown as undefined,
})
@Injectable()
export class ItemResourceState<Entity extends ItemResource> {
  static read<Entity extends ItemResource>(link: Link) {
    return createSelector([ItemResourceState], (state: ItemResourceStateModel<Entity>) => state?.get(link.href));
  }

  constructor(private readonly itemResourceWebService: ItemResourceWebService<Entity>) {}

  @Action(ItemResourceCreate)
  doCreate(
    context: StateContext<ItemResourceStateModel<Entity>>,
    { link, entity }: ItemResourceCreate<Entity>,
  ): Observable<unknown> {
    return this.itemResourceWebService.create(link, entity).pipe(
      map(value => new Map(context.getState()?.entries() ?? []).set(value._links.self.href, value)),
      map(context.setState),
    );
  }

  @Action(ItemResourceRead)
  doRead(context: StateContext<ItemResourceStateModel<Entity>>, { link }: ItemResourceRead): Observable<unknown> {
    if (context.getState()?.has(link.href)) {
      return EMPTY;
    }

    context.setState(new Map(context.getState()?.entries() ?? []).set(link.href, undefined));
    return this.itemResourceWebService.read({ href: `${link.href}?_extends=children` }).pipe(
      map(value => new Map(context.getState()?.entries() ?? []).set(`${value._links.self.href}`, value)),
      map(context.setState),
    );
  }

  @Action(ItemResourceUpdate)
  doUpdate(context: StateContext<ItemResourceStateModel<Entity>>, { link, entity }: ItemResourceUpdate<Entity>) {
    context.setState(new Map(context.getState()?.entries() ?? []).set(link.href, entity));
  }

  @Action(ItemResourceRemove)
  doRemove(context: StateContext<ItemResourceStateModel<Entity>>, { link }: ItemResourceRemove) {
    context.getState()?.delete(link.href);
    context.setState(new Map(context.getState()?.entries() ?? []));
  }
}
